import React, { useCallback, useId, useRef } from 'react';

import {
  Checkbox,
  CheckBoxProps
} from '@cube3/cubicle/src/core/atoms/Checkbox/Checkbox';
import {
  HBox,
  Item,
  VBox
} from '@cube3/cubicle/src/core/templates/layout/Flex';
import {
  Typography,
  TypographyProps
} from '@cube3/cubicle/src/core/atoms/Typography';
import { Tooltip } from '@cube3/cubicle/src/core/atoms/Tooltip/Tooltip';
import { Icon } from '@cube3/cubicle/src/core/atoms/Icon';
import { SizeVariants } from '@cube3/cubicle/src/theme/themes';
import { FieldWithState } from '../types/GenericFormTypes';
import { add } from '@cube3/common/utils/add';
import { remove } from '@cube3/common/utils/remove';
import { shouldShowErrors } from '../utils/shouldShowErrors';
import { getReadableErrors } from '../utils/getErrorMessage';

export type CheckboxFieldProps = Pick<CheckBoxProps, 'size'> & {
  onClick?: () => void;
  labelStyle?: TypographyProps;
  tooltip?: React.ReactNode;
  options?: Array<{
    id: string;
    value: string;
    tooltip?: boolean;
    disabled?: boolean;
  }>;
  /** Description of a checkbox */
  text?: React.ReactNode;
  /** The maximum options you can select if `multiple` is true */
  maxLength?: number;
};
type Props = FieldWithState<boolean | string[], 'checkbox'>;

const emptyObj = {} as any;
const customColor = (theme) => theme.color.text['03'];

export const CheckboxField = (props: Props) => {
  const {
    options,
    showErrors = 'touched',
    touched,
    active,
    error,
    onBlur,
    value
  } = props;

  const sse = shouldShowErrors(showErrors, touched, active);
  const errors = getReadableErrors(error);
  const errorMessage = typeof errors === 'string' ? errors : errors?.[0];

  const handleBlur = useCallback(
    (ev) => {
      onBlur(value);
    },
    [value, onBlur]
  );

  if (options && options.length) {
    return (
      <MultipleCheckbox
        {...props}
        error={errorMessage}
        showErrors={sse}
        onBlur={handleBlur}
      />
    );
  } else {
    return (
      <SingleCheckbox
        {...props}
        error={errorMessage}
        showErrors={sse}
        onBlur={handleBlur}
      />
    );
  }
};

const SingleCheckbox = (props: Props) => {
  const {
    text,
    size,
    labelStyle = emptyObj,
    tooltip,
    onClick,
    onBlur,
    showErrors,
    touched,
    active,
    error,
    ...extraProps
  } = props;

  const id = useId();


  return (
    <VBox gap={4} /*align={!text ? 'end' : undefined}*/>
      <HBox
        gap={8}
        align="center"
        justify="start"
        style={{ margin: '4px 0 0 4px' }}
        onClick={onClick}
        onBlur={onBlur}
      >
        <Checkbox
          id={id}
          {...extraProps}
          size={size}
          text={text}
          typographyProps={{
            colorVariant: '03',
            lineHeight: 'trim',
            variant: size,
            ...labelStyle
          }}
        />
        {tooltip ? (
          <Tooltip content={tooltip} placement="top">
            <Icon
              icon="help"
              size={SizeVariants.sm}
              customColor={customColor}
            />
          </Tooltip>
        ) : null}
      </HBox>
      {error && showErrors && (
        <Typography
          colorVariant="03"
          lineHeight="trim"
          color="critical"
          variant={size}
          {...labelStyle}
        >
          {error}
        </Typography>
      )}
    </VBox>
  );
};
const initialValue: string[] = [];

const MultipleCheckbox = (props: Props) => {
  const {
    text,
    size,
    labelStyle = emptyObj,
    tooltip,
    onClick,
    options,
    showErrors = 'touched',
    visible,
    label,
    //meta
    touched,
    active,
    error,
    // input
    value = initialValue,
    onChange,
    ...extraProps
  } = props;

  const handleSelect = useCallback(
    (option) => (c) => {
      const val = value || []
      onChange(c ? add(val, option.value) : remove(val, option.value))
    },
    [value]
  );

  return (
    <VBox gap={16} style={{ width: 320 }}>
      {options.map((o) => (
        <Item key={o.id} style={{ width: '250px' }}>
          <CheckboxField
            {...extraProps}
            error={error}
            touched={touched}
            active={active}
            name={o.id}
            type="checkbox"
            value={value.includes?.(o.value)}
            onChange={handleSelect(o)}
            visible={visible}
            label={label}
            text={o.value || o.id}
            disabled={o.disabled}
            tooltip={o.tooltip}
            size={SizeVariants.md}
            labelStyle={{ colorVariant: '01' }}
            showErrors={false}
          />
        </Item>
      ))}
      {error && showErrors && (
        <Typography
          colorVariant="03"
          lineHeight="trim"
          color="critical"
          variant={size}
          {...labelStyle}
        >
          {error}
        </Typography>
      )}
    </VBox>
  );
};
